import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

import {
  Container,
  Card,
  Row,
  Button,
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";

import { toast } from "react-toastify";
import { STUDENT_FEEDBACK_API } from "../../../api/api";

import courseImg from "../../../assets/student/home-frontend.png";
import frontend from "../../../assets/publicpageimg/frontend-course.png";
import backend from "../../../assets/publicpageimg/backend-course.png";
// import courseIcon from "../../../assets/student/courses-icon.png";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentFeedback.css";

const StudentFeedback = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState({});
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");

  const [showModal, setShowModal] = useState(false);

  //loading
  const [loadingFeedback, setLoadingFeedback] = useState(true);
  const [loadingFeedbackSubmitting, setLoadingFeedbackSubmitting] =
    useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchFeedback = useCallback(async () => {
    // Map categories to local images
    const courseImages = {
      "Frontend Development": frontend,
      "Backend Development": backend,
      "MERN Stack Development": courseImg,
      "Digital Marketing": courseImg,
      "Data Analytics": courseImg,
      "UI/UX": courseImg,
    };

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setLoadingFeedback(false);
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        STUDENT_FEEDBACK_API.GET_STUDENT_COURSE_FEEDBACK,
        {
          student_id: userId,
          course_id: courseId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200 && data.data) {
        setFeedback({
          courseId: data.data.course_id,
          courseTitle: data.data.course_title,
          noOfModules: data.data.no_of_modules || 0,
          duration: data.data.formatted_duration,
          deadline: data.data.duration,
          tutorId: data.data.tutor_id,
          tutorName: data.data.tutor_name,
          feedbackId: data.data.feedback_id || null,
          tutorResponse: data.data.response,
          courseImage: courseImages[data.data.course_title] || courseImg,
        });

        setRating(data.data.rating || 0);
        setFeedbackText(data.data.review || "");
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      if (error.response?.status === 404) {
        setTimeout(() => {
          navigate("/student/courses");
        }, 1000);
        return; // ← handle 404 separately
      }
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setLoadingFeedback(false);
    }
  }, [courseId, navigate]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const isFeedbackSubmitted = !!feedback.feedbackId;

  const handleFeedbackSubmit = async () => {
    if (!rating) {
      showErrorToast("Please provide rating ");
      return;
    }

    if (feedbackText.length < 30) {
      showErrorToast("Feedback must be at least 30 characters.");
      return;
    }

    if (feedbackText && !/[a-zA-Z]/.test(feedbackText)) {
      showErrorToast("Feedback must contain at least one letter");
      return;
    }

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!token || !userId) {
      navigate("/signin");

      return;
    }

    setLoadingFeedbackSubmitting(true);
    try {
      const response = await axios.post(
        STUDENT_FEEDBACK_API.ADD_FEEDBACK,
        {
          student_id: userId,
          tutor_id: feedback.tutorId,
          course_id: feedback.courseId,
          rating: rating,
          review: feedbackText,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        await fetchFeedback();

        setShowModal(true);
        setTimeout(() => setShowModal(false), 4000);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Feedback error:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setLoadingFeedbackSubmitting(false);
    }
  };

  if (loadingFeedback) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // Format "HH:MM:SS" to user-friendly string
  const formatDuration = (duration) => {
    if (!duration) return "0 min";
    const [hours, minutes] = duration.split(":").map(Number); //seconds
    let result = "";
    if (hours > 0) result += `${hours} hr `;
    if (minutes > 0) result += `${minutes} min `;
    // if (seconds > 0) result += `${seconds} sec`;
    if (!hours && !minutes) result = "1 min"; // at least 1 min && !seconds
    return result.trim();
  };

  return (
    <Container fluid className="student-certificate-section-container">
      <div className="student-feedback-backmoving-button-wrapper">
        <button
          className="student-dashboard-backmoving-button-main"
          onClick={() => navigate(-1)}
        >
          <IoArrowBack className="student-dashboard-backmoving-button-icon" />
          <span className="student-dashboard-backmoving-button-text">Back</span>
        </button>
      </div>

      {/* HEADER */}
      <Row className="mb-3 mt-0">
        <h2 className="student-certificate-section-title">Feedback</h2>
        <p className="student-certificate-section-description">
          Share your feedback and help us improve your learning experience.
        </p>
      </Row>

      <Card className="student-feedback-card">
        <Card.Body>
          <div className="student-feedback-course">
            <div className="student-feedback-course-img-content">
              <img
                src={feedback.courseImage || courseImg}
                alt="course"
                className="student-feedback-course-img"
              />

              <div className="student-feedback-course-text">
                <div className="student-feedback-course-label">
                  {/* <img
                    src={courseIcon}
                    alt="course-icon"
                    className="student-feedback-course-iconimg"
                  /> */}
                  <span>Course</span>
                </div>

                <h3
                  className="student-feedback-course-title"
                  title={feedback.courseTitle}
                >
                  {feedback.courseTitle || "N/A"}
                </h3>
              </div>
            </div>

            <div className="student-feedback-meta-grid">
              <div className="student-feedback-course-meta-item">
                <span className="student-feedback-course-meta-label">
                  Content
                </span>
                <span className="student-feedback-course-meta-value">
                  {feedback.noOfModules} Modules
                </span>
              </div>

              <div className="student-feedback-course-meta-item">
                <span className="student-feedback-course-meta-label">
                  Duration
                </span>
                <span
                  className="student-feedback-course-meta-value"
                  title={formatDuration(feedback.duration)}
                >
                  {formatDuration(feedback.duration)}
                </span>
              </div>

              <div className="student-feedback-course-meta-item">
                <span className="student-feedback-course-meta-label">
                  Deadline
                </span>
                <span className="student-feedback-course-meta-value">
                  {feedback.deadline}
                </span>
              </div>

              <div className="student-feedback-course-meta-item">
                <span className="student-feedback-course-meta-label">
                  Tutor
                </span>
                <span
                  className="student-feedback-course-meta-value"
                  title={feedback.tutorName}
                >
                  {feedback.tutorName}
                </span>
              </div>

              {/* RATING – SAME STYLE */}
              <div className="student-feedback-course-meta-item">
                <span className="student-feedback-course-meta-label">
                  Rating
                </span>
                <div className="student-feedback-course-meta-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={rating >= star ? "star active" : "star"}
                      onClick={() => !isFeedbackSubmitted && setRating(star)}
                      style={{
                        cursor: isFeedbackSubmitted ? "not-allowed" : "pointer",
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* FEEDBACK */}
          <div className="student-feedback-form mt-4">
            {/* <h5 className="student-feedback-form-title">Feedback</h5> */}
            <p className="student-feedback-form-paragraph">
              We value your feedback. Tell us what you liked and how we can
              improve.
            </p>

            <Form.Control
              as="textarea"
              rows={3}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              readOnly={isFeedbackSubmitted}
            />

            {!isFeedbackSubmitted && (
              <div className="student-feedback-btn">
                <Button
                  onClick={handleFeedbackSubmit}
                  className="student-feedback-submit-btn"
                  disabled={loadingFeedbackSubmitting}
                >
                  {loadingFeedbackSubmitting
                    ? "Submitting..."
                    : "Submit Feedback"}
                </Button>
              </div>
            )}

            {isFeedbackSubmitted && feedback.tutorResponse && (
              <div className="student-feedback-response-box">
                <div className="student-feedback-response-title">
                  Tutor Response:
                </div>

                <div className="student-feedback-response-text">
                  {feedback.tutorResponse}
                </div>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* SUCCESS MODAL */}
      <Modal
        show={showModal}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="student-feedback-modal"
      >
        <Modal.Body className="student-feedback-modal-body">
          <div className="student-feedback-modal-icon">✓</div>

          <h4 className="student-feedback-modal-title">Feedback Submitted</h4>

          <p className="student-feedback-modal-text">
            Thank you for sharing your feedback. Your input helps us improve the
            learning experience.
          </p>

          <Button
            className="student-feedback-modal-btn"
            onClick={() => setShowModal(false)}
          >
            Close
          </Button>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default StudentFeedback;
